import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {DefaultFilter, ViewCell} from '@mominsamir/ngx-smart-table';
import {BehaviorSubject, Observable, of, Subject, Subscription} from 'rxjs';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {switchMap, takeUntil} from 'rxjs/operators';
import {APIResponse, PagingResponse} from '@core/interfaces/system/system-common';

@Component({
    selector: 'ngx-check-box-cell',
    template: `
        <div class="action-cell">
            <nb-checkbox
                [disabled]="disabled(rowData)"
                [checked]="isChecked"
                [ngClass]="(loadedDataTableFlag.asObservable() | async) ? 'check-box' : 'disable-check-box'"
                (checkedChange)="onClick($event)"
            ></nb-checkbox>
        </div>
    `,
    styleUrls: ['./check-box-cell.component.scss'],
})
export class CheckBoxCellComponent<T> extends Unsubscribable implements ViewCell, OnInit {
    @Input() value: string | number;
    @Input() rowData: T;
    @Input() selectedRows$: Observable<T[]>;
    @Input() keyColumnName: string = 'id';
    @Input() multipleSelect: false;
    @Output() onSelectChange: EventEmitter<{selected: boolean; rowData: T}> = new EventEmitter();
    loadedDataTableFlag: BehaviorSubject<boolean> = new BehaviorSubject(false);

    isChecked: boolean = false;

    disabled = (rowData: any): boolean => false;

    constructor(private cd: ChangeDetectorRef) {
        super();

        // If user select the checkbox during the reloading datatable time, it will be causing an error.
        setTimeout(() => {
            this.loadedDataTableFlag.next(true);
        }, 500);
    }

    public ngOnInit() {
        this.selectedRows$.pipe(takeUntil(this.unsubscribe$)).subscribe((rows: T[]) => {
            this.checkSelectedValues(this.rowData[this.keyColumnName], rows);
        });
    }

    checkSelectedValues(value, array) {
        this.isChecked = array.some((item) => {
            if (typeof value === 'object') return item[this.keyColumnName] === value.value;
            return item[this.keyColumnName] === value;
        });
        this.cd.markForCheck();
    }

    onClick(selected: boolean) {
        this.onSelectChange.emit({selected: selected, rowData: this.rowData});
    }
}

@Component({
    template: `
        <div class="action-cell">
            <nb-checkbox
                [indeterminate]="checkIfAllSelected()"
                [checked]="checkIfAllSelected()"
                [ngClass]="(loadedDataTableFlag.asObservable() | async) ? 'check-box' : 'disable-check-box'"
                (checkedChange)="toggle($event)"
            ></nb-checkbox>
        </div>
    `,
})
export class CheckBoxSelectAllComponent<T> extends DefaultFilter implements OnInit, OnChanges, OnDestroy {
    selectedRows$: Observable<T[]> = of([]);
    toggleAllItems = (selected: boolean, cachedData?: T[]) => {};
    checkIfAllSelected = () => false;

    loadedDataTableFlag: BehaviorSubject<boolean> = new BehaviorSubject(false);
    subscription: Subscription;

    // API functions and cache for pre-fetching
    private getAllIdsFn: () => Observable<APIResponse<number[]>>;
    private getDataByIdsFn: (ids: number[]) => Observable<PagingResponse<T>>;
    private cachedData: T[] = [];
    private unsubscribe$ = new Subject<void>();

    constructor(private changeDetectorRef: ChangeDetectorRef) {
        super();

        // If user select the checkbox during the reloading datatable time, it will be causing an error.
        setTimeout(() => {
            this.loadedDataTableFlag.next(true);
        }, 500);
    }

    ngOnInit(): void {
        const config = this.column?.filter?.config;
        this.toggleAllItems = config?.toggleAllItems;
        this.checkIfAllSelected = config?.checkIfAllSelected;
        this.getAllIdsFn = config?.getAllIdsFn;
        this.getDataByIdsFn = config?.getDataByIdsFn;

        // Pre-fetch data if API functions are provided
        if (this.getAllIdsFn && this.getDataByIdsFn) {
            this.preFetchData();
        }
    }

    /**
     * Pre-fetch all form IDs and data on page load
     */
    private preFetchData(): void {
        // const observable = this.getAllIdsFn();
        this.getAllIdsFn()
            .pipe(
                takeUntil(this.unsubscribe$),
                switchMap((response: APIResponse<number[]>) => {
                    const allFormIds = response.response;
                    // Handle empty case
                    if (!allFormIds || allFormIds.length === 0) {
                        return of({
                            items: [],
                            pageNumber: 1,
                            pageSize: 0,
                            startRecordNumber: 0,
                            totalCount: 0,
                            totalFilteredRecords: 0,
                        });
                    }

                    // Fetch full form data for all IDs
                    return this.getDataByIdsFn(allFormIds);
                }),
            )
            .subscribe({
                next: (dataSource: PagingResponse<T>) => {
                    this.cachedData = dataSource.items;
                    this.changeDetectorRef.markForCheck();
                },
                error: (error) => {
                    console.error('Error fetching all forms on load:', error);
                    this.changeDetectorRef.markForCheck();
                },
            });
    }

    /**
     * Toggle selection - passes cached data to callback
     */
    toggle(selected: boolean): void {
        // Pass cached data to callback if available
        this.toggleAllItems(selected, this.cachedData.length > 0 ? this.cachedData : undefined);
    }

    public ngOnChanges(changes: SimpleChanges) {
        // Don't delete, this method is needed for the library work
        let i = 1;
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
        this.subscription?.unsubscribe();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
